import { Avatar, Card, Icon, IconButton, Tooltip, Typography } from "@/components/atoms";
import { useAppDispatch } from "@/redux/hooks";
import { setInlineEditing } from "@/redux/slices/booking/bookingSlice";
import { BookingDetails } from "@/redux/slices/booking/types";
import { minutesToHoursToDaysExtended, getAssetUrl, formatCurrency } from "@/utils";
import { DetailField } from "../../../common/DetailField";
import { renderVerificationBadge } from "../../../helpers";
import { useIsOperatorOwnJob } from "../../../hooks/useIsOperatorOwnJob";

interface VehicleSectionProps {
  booking: BookingDetails;
}

export const VehicleSection = ({ booking }: VehicleSectionProps) => {
  const dispatch = useAppDispatch();
  const isOwnJob = useIsOperatorOwnJob();
  const { driver, assignedVehicle, vehicleCategory, transferType, cost, metadata } = booking;
  const currentVehicle = driver?.vehicle;
  const { isVehicleCorrect, isVehicleVerified, isVehicleEditAllowed, isOffloaded } = metadata;

  const formatVehicleDisplay = (vehicle: typeof currentVehicle | typeof assignedVehicle) => {
    if (!vehicle) return "-";
    return `${vehicle.year} ${vehicle.model.make} ${vehicle.model.model} (${vehicle.registrationNumber || "N/A"})`;
  };

  const formatTransferType = () => {
    return ["hourly_rate", "daily_rate"].includes(transferType.type)
      ? `${transferType.name} (${minutesToHoursToDaysExtended(transferType.duration)})`
      : transferType.name;
  };

  const showVehicleWarning = assignedVehicle && currentVehicle ? assignedVehicle.uuid !== currentVehicle.uuid : !isVehicleCorrect;

  const handleEditMode = () => {
    dispatch(setInlineEditing("change-vehicle"));
  };

  const enableEditMode = isVehicleEditAllowed;

  return (
    <Card className="space-y-3 p-2.5">
      {currentVehicle ? (
        <DetailField
          leading={<Avatar src={getAssetUrl(currentVehicle.model.avatar)} alt={`${currentVehicle.model.make} logo`} size="xs" />}
          label="Current Vehicle"
          value={
            <Typography variant="paragraph" className="flex items-center gap-1">
              <span>{formatVehicleDisplay(currentVehicle)}</span>
              {currentVehicle.color && (
                <Tooltip content={`Color: ${currentVehicle.color}`} placement="top">
                  <div className="h-4 w-4 rounded-full border border-neutral-black" style={{ backgroundColor: currentVehicle.color }} />
                </Tooltip>
              )}
              {renderVerificationBadge(isVehicleVerified)}
              {showVehicleWarning && (
                <Tooltip content="Driver is not in the correct vehicle" placement="top">
                  <Icon name="Danger" variant="Bold" size="sm" className="text-warning" />
                </Tooltip>
              )}
            </Typography>
          }
        />
      ) : (
        <DetailField leading={<Icon name="Car" size="lg" className="text-neutral-dark-gray" />} label="Current Vehicle" value="-" />
      )}

      {(!isOffloaded || isOwnJob) && (
        <DetailField
          leading={
            assignedVehicle ? (
              <Avatar src={getAssetUrl(assignedVehicle.model.avatar)} alt={`${assignedVehicle.model.make} logo`} size="xs" />
            ) : (
              <Icon name="Car" size="lg" className="text-neutral-dark-gray" />
            )
          }
          label="Assigned Vehicle"
          value={
            <Typography variant="paragraph" className="flex items-center gap-1">
              <span>{formatVehicleDisplay(assignedVehicle)}</span>
              {assignedVehicle && assignedVehicle.color && (
                <Tooltip content={`Color: ${assignedVehicle.color}`} placement="top">
                  <div className="h-4 w-4 rounded-full border border-neutral-black" style={{ backgroundColor: assignedVehicle.color }} />
                </Tooltip>
              )}
            </Typography>
          }
          right={
            enableEditMode && (
              <Tooltip content="Update Assigned Vehicle" placement="top">
                <IconButton iconName="Edit2" iconSize="md" size="xs" variant="primary" className="h-8 w-8" onClick={handleEditMode} />
              </Tooltip>
            )
          }
        />
      )}

      <DetailField
        leading={<Icon name="Car" size="lg" className="text-neutral-dark-gray" />}
        label="Transfer Type"
        value={formatTransferType()}
      />

      <DetailField
        leading={<Icon name="Car" size="lg" className="text-neutral-dark-gray" />}
        label="Vehicle Category"
        value={vehicleCategory.name}
      />

      {!isNaN(Number(cost.percentageToVehicle)) && (
        <DetailField
          leading={<Icon name="DollarCircle" size="lg" className="text-neutral-dark-gray" />}
          label="Vehicle %"
          labelInlineRight={
            <Tooltip content="Percentage of the total cost that the vehicle will receive" placement="top">
              <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
            </Tooltip>
          }
          value={`${cost.percentageToVehicle}%`}
        />
      )}

      <DetailField
        leading={<Icon name="DollarCircle" size="lg" className="text-neutral-dark-gray" />}
        label="Vehicle Payment"
        labelInlineRight={
          <Tooltip content="Calculated as % of the driver payment as per your vehicle settings" placement="top">
            <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
          </Tooltip>
        }
        value={cost.breakdown.vehiclePayment ? formatCurrency(cost.breakdown.vehiclePayment, 2, cost.currency) : "-"}
      />
    </Card>
  );
};
